<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import Card from '@uiKit/components/Card.vue'
import EmptyState from '@uiKit/components/EmptyState.vue'
import InfoBanner from '@uiKit/components/InfoBanner.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import Text from '@uiKit/elements/Text.vue'
import { formatDisplayDate } from '@shared/utils'
import {
  isAuthorizationUnchecked,
  peerAuthorizationColor,
  peerAuthorizationDetail,
  peerAuthorizationLabel,
} from '@shared/constants/trustlineAuthorization'
import { trustlineStatusColor, trustlineStatusLabel } from '../constants/trustlineStatus'
import type { TrustlineGap } from '../types/cryptoWalletTypes'

interface Props {
  gaps: TrustlineGap[]
  /** `TrustlineGap.key` currently being acted on — spins just that card. */
  busyGapKey: string
  /** propertyId to highlight, set when an order redirected the operator here. */
  highlightPropertyId: string
  /**
   * Distinguished from "nothing to do" because an empty list mid-load reads as
   * "this wallet needs no trustlines", which is the one conclusion an operator
   * must not draw by accident.
   */
  loading?: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'setup', gap: TrustlineGap): void
  (e: 'check', gap: TrustlineGap): void
}>()

const SKELETON_ROWS = 2

// Reads as a sentence on the card: "no offering, token currency code or issuer
// address". Built here rather than in the model because it is copy, and the
// model already carries the list the copy is made of.
function missingSummary(fields: string[]): string {
  if (fields.length <= 1) return fields.join('')
  return `${fields.slice(0, -1).join(', ')} or ${fields[fields.length - 1]}`
}

// How long the VAISP has been waiting on this property's opt-in. The age of the
// gap, not a reference to any order — order identity lives on the orders screen.
function blockedSummary(gap: TrustlineGap): string {
  // `formatDisplayDate` renders an em dash for a missing date, which would read
  // as "Waiting since —"; a dateless row says only that it is waiting.
  return gap.blockedSince
    ? `Waiting since ${formatDisplayDate(gap.blockedSince)}`
    : 'Waiting on this wallet to opt in'
}

// An empty highlight param must not match the id-less cards, which all carry
// `propertyId: ''` — that outlined every one of them on a plain visit.
function isHighlighted(gap: TrustlineGap, highlightPropertyId: string): boolean {
  return Boolean(highlightPropertyId) && gap.propertyId === highlightPropertyId
}
</script>

<template>
  <Box flex direction="col" gap="3">
    <Box v-if="loading" flex direction="col" gap="3">
      <Skeleton v-for="row in SKELETON_ROWS" :key="row" height="7rem" />
    </Box>

    <EmptyState
      v-else-if="gaps.length === 0"
      icon="check-circle"
      size="compact"
      title="No trustline is waiting to be set up"
      description="The VAISP is not holding any of this investor's orders at the opt-in step, so there is no trustline for this wallet to open."
    />

    <Card
      v-for="gap in gaps"
      :key="gap.key"
      variant="outlined"
      padding="4"
      :class="{ highlighted: isHighlighted(gap, highlightPropertyId) }"
    >
      <Box flex direction="col" gap="3">
        <Box flex justify="between" align="start" gap="3">
          <Box flex direction="col" gap="1">
            <Text type="body" weight="medium">{{ gap.propertyName }}</Text>
            <Text type="caption" color="muted">
              {{ gap.offeringTitle ? `${gap.offeringTitle} · ` : '' }}{{ blockedSummary(gap) }}
            </Text>
          </Box>
          <!-- Two badges because they answer two questions: does the line exist
               on the ledger, and has the issuer authorised it (WAL-06). A line
               can be `active` and unauthorised, and the second badge is the only
               thing that says so. -->
          <Box flex direction="col" align="end" gap="1">
            <StatusBadge
              :color="trustlineStatusColor(gap.trustlineStatus)"
              :label="trustlineStatusLabel(gap.trustlineStatus)"
            />
            <StatusBadge
              :color="peerAuthorizationColor(gap.peerAuthorized)"
              :label="peerAuthorizationLabel(gap.peerAuthorized)"
            />
          </Box>
        </Box>

        <!-- The sentence behind the badge, for the two states that need one.
             `info` in both cases, never `danger` or `warning`: an unread ledger
             is not a problem and a wait on Ctrl-Alt is not a failure. An
             already-authorised line needs no explanation, so the badge alone
             carries it and the card stays readable.
             `peerAuthorizationDetail` owns the wording — nothing here branches
             on the flag beyond deciding whether to speak. -->
        <InfoBanner
          v-if="gap.peerAuthorized !== true"
          variant="info"
          :title="peerAuthorizationLabel(gap.peerAuthorized)"
          :message="peerAuthorizationDetail(gap.peerAuthorized)"
        />

        <!-- The token identity the TrustSet is opened against. Shown in full
             because a mismatched issuer or currency code is the first thing an
             operator checks when a trustline does not behave. -->
        <Box flex direction="col" gap="1">
          <Text type="caption" color="muted">TOKEN</Text>
          <Text type="body-sm">{{ gap.tokenSymbol || '—' }}</Text>
          <Text type="caption" color="muted">ISSUER</Text>
          <Text type="body-sm" family="mono" truncate>{{ gap.issuerWalletAddress || '—' }}</Text>
          <Text type="caption" color="muted">CURRENCY (HEX)</Text>
          <Text type="body-sm" family="mono" truncate>{{ gap.tokenHex || '—' }}</Text>
        </Box>

        <!-- Named on the card rather than left as a greyed-out button, because
             the fix is not on this screen: the offering has to be published or
             re-registered before any trustline can be opened for it. -->
        <InfoBanner
          v-if="gap.missingTokenFields.length > 0"
          variant="warning"
          title="No trustline can be opened yet"
          :message="`This property has no ${missingSummary(gap.missingTokenFields)} recorded, so there is no token to trust. Check the offering is live and registered with the VAISP, then come back.`"
        />

        <Box flex gap="2">
          <!-- Setup is offered when there is no line yet or the last attempt
               failed; a line already in flight offers the check instead, because
               submitting a second TrustSet for it would do nothing.

               `label` rather than slot content: PrimeVue renders a default slot
               INSTEAD of its own label span, so the text loses the
               `p-button-label` sizing and wraps inside a button that no longer
               grows to fit it. Every other call site in the app passes `label`. -->
          <Button
            v-if="gap.requiresTrustlineSetup"
            label="Set up trustline"
            color="primary"
            icon="link"
            :loading="busyGapKey === gap.key"
            :disabled="!gap.canSetupTrustline"
            @click="emit('setup', gap)"
          />
          <!-- Gated on the offering alone, not on `canSetupTrustline`: the check
               re-reads the ledger and admin-service resolves the issuer and
               currency code from the offering itself, so an incomplete identity
               on this card does not stop it. Gating it the same way as setup
               disabled the one button a disputed card exists to offer.

               Promoted to a solid primary when the issuer authorisation has
               never been read: reading the ledger is then the whole action the
               card has to offer, and a de-emphasised outline button next to a
               "Not checked" badge reads as optional when it is the next step. -->
          <Button
            v-else
            label="Check status"
            :variant="isAuthorizationUnchecked(gap.peerAuthorized) ? 'solid' : 'outline'"
            :color="isAuthorizationUnchecked(gap.peerAuthorized) ? 'primary' : 'secondary'"
            icon="refresh"
            :loading="busyGapKey === gap.key"
            :disabled="!gap.offeringId"
            @click="emit('check', gap)"
          />
        </Box>
      </Box>
    </Card>
  </Box>
</template>

<style scoped>
.highlighted {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 2px;
}
</style>
